import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface Contact {
  id: number;
  full_name: string;
  email: string | null;
  job_title: string | null;
  linkedin_url: string | null;
  phone: string | null;
  priority: number;
  legacy_label: string | null;
  stage: string;
  source: string;
  notes: string | null;
  company_id: number | null;
  company_name: string | null;
  company_domain: string | null;
  company_is_customer: number | null;
  last_demo_at: string | null;
  next_demo_at: string | null;
  demo_count: number;
}

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const formatDaysAgo = (iso: string | null): string => {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
};

const formatDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export const ContactsView: React.FC = () => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [era, setEra] = useState<'all' | 'current' | 'V1 Demo'>('all');

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (search.trim()) qs.set('search', search.trim());
      if (era !== 'all') qs.set('era', era);
      const res = await fetch(`/api/crm/contacts?${qs}`, { headers: authHeaders() });
      const data = await res.json();
      setContacts(data.contacts || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [era]);

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-3 border-b border-gray-100 flex flex-wrap items-center gap-3">
        <form onSubmit={e => { e.preventDefault(); load(); }} className="flex-1 min-w-[200px]">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search contacts by name, email or company…"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </form>
        <select
          value={era}
          onChange={e => setEra(e.target.value as any)}
          className="px-2 py-2 border border-gray-300 rounded text-xs"
        >
          <option value="all">All eras</option>
          <option value="current">Current (no legacy label)</option>
          <option value="V1 Demo">V1 Demo only</option>
        </select>
        <button onClick={load} className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
          Search
        </button>
      </div>
      {loading ? (
        <div className="p-8 text-center text-gray-500">Loading…</div>
      ) : contacts.length === 0 ? (
        <div className="p-8 text-center text-gray-400 italic">No contacts.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-gray-500 border-b">
              <tr>
                <th className="py-2 px-3">Contact</th>
                <th className="py-2 px-3">Company</th>
                <th className="py-2 px-3">Email</th>
                <th className="py-2 px-3 text-center">Priority</th>
                <th className="py-2 px-3 text-right">Demos</th>
                <th className="py-2 px-3 text-right">Last demo</th>
                <th className="py-2 px-3 text-right">Next demo</th>
                <th className="py-2 px-3">Tags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {contacts.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => window.location.href = `/admin/crm/contacts/${c.id}`}>
                  <td className="py-2 px-3">
                    <div className="font-medium text-blue-700 hover:underline">{c.full_name}</div>
                    {c.job_title && <div className="text-xs text-gray-500">{c.job_title}</div>}
                  </td>
                  <td className="py-2 px-3 text-gray-700">
                    {c.company_id ? (
                      <Link
                        to={`/admin/crm/companies/${c.company_id}`}
                        className="text-blue-700 hover:underline"
                      >
                        {c.company_name}
                      </Link>
                    ) : (
                      <span className="text-gray-400 italic">—</span>
                    )}
                    {c.company_is_customer && (
                      <span className="ml-1.5 inline-block px-1 py-0.5 bg-green-100 text-green-700 rounded text-[9px] font-medium align-middle">
                        Customer
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-gray-500 text-xs truncate max-w-[200px]">{c.email || '—'}</td>
                  <td className="py-2 px-3 text-center text-xs">
                    {c.priority > 0 ? '🔥'.repeat(c.priority) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="py-2 px-3 text-right text-gray-700">{c.demo_count}</td>
                  <td className="py-2 px-3 text-right text-xs text-gray-500">{formatDaysAgo(c.last_demo_at)}</td>
                  <td className="py-2 px-3 text-right text-xs text-gray-500">{formatDateTime(c.next_demo_at)}</td>
                  <td className="py-2 px-3">
                    <div className="flex flex-wrap gap-1">
                      {c.legacy_label && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px] font-medium">{c.legacy_label}</span>
                      )}
                      {c.source === 'outlook_sync' && (
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] font-medium">📅</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="px-3 py-2 border-t border-gray-100 text-xs text-gray-500">
        {contacts.length} contacts
      </div>
    </div>
  );
};
